import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal, Tab, Tabs } from 'react-bootstrap';
import styled from 'styled-components';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { Button } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import {
  DASHBOARD_TIMER_MIN_MINUTES,
  dashboardCardVisibilityOptions,
  getDefaultDashboardCardVisibility,
  normalizeDashboardTimerSettings
} from '../config/cardVisibilityConfig';

const formatCountdown = (seconds) => {
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const DashboardSettingsModal = ({
  show,
  values,
  timerSettings,
  timerRunning,
  timerRemainingSeconds,
  timerLoading,
  cardOptions,
  onClose,
  onSaveCards,
  onSaveTimer,
  onToggleTimer,
  onRefreshTimer
}) => {
  const [activeTab, setActiveTab] = useState('cards');
  const [localValues, setLocalValues] = useState(values);
  const [localTimerSettings, setLocalTimerSettings] = useState(timerSettings);

  useEffect(() => {
    setLocalValues(values);
  }, [values, show]);

  useEffect(() => {
    setLocalTimerSettings(timerSettings);
  }, [timerSettings, show]);

  const handleToggleCard = (cardId) => {
    setLocalValues((current) => ({
      ...current,
      [cardId]: !current?.[cardId]
    }));
  };

  const handleReset = () => {
    setLocalValues(getDefaultDashboardCardVisibility(cardOptions));
  };

  const handleSave = () => {
    onSaveCards(localValues);
  };

  const handleTimerIntervalChange = (value) => {
    setLocalTimerSettings({
      intervalMinutes: value
    });
  };

  const handleSaveTimer = () => {
    onSaveTimer(normalizeDashboardTimerSettings(localTimerSettings));
  };

  return (
    <Modal show={show} onHide={onClose} animation={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>Configuracoes do dashboard</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SettingsTabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'cards')} className="mb-3">
          <Tab eventKey="cards" title="Cards">
            <TabIntro>Selecione os cards que devem aparecer no dashboard.</TabIntro>
            <CardsList>
              {cardOptions.map((option) => (
                <CardOption key={option.id}>
                  <Form.Check
                    type="checkbox"
                    id={`dashboard-card-${option.id}`}
                    label={option.label}
                    checked={!!localValues?.[option.id]}
                    onChange={() => handleToggleCard(option.id)}
                  />
                </CardOption>
              ))}
            </CardsList>
          </Tab>
          <Tab eventKey="timer" title="Temporizador">
            <TabIntro>
              Configure de quanto em quanto tempo o dashboard deve refazer as consultas.
            </TabIntro>
            <TimerPanel>
              <Form.Group>
                <Form.Label>Intervalo de atualizacao em minutos</Form.Label>
                <Form.Control
                  type="number"
                  min={DASHBOARD_TIMER_MIN_MINUTES}
                  step={1}
                  value={localTimerSettings?.intervalMinutes ?? ''}
                  onChange={(event) => handleTimerIntervalChange(event.target.value)}
                />
                <TimerHint>O intervalo minimo permitido e de {DASHBOARD_TIMER_MIN_MINUTES} minuto.</TimerHint>
              </Form.Group>
              <TimerStatus>
                <TimerClock>
                  <Icon name="timer" size={16} />
                  <strong>{formatCountdown(timerRemainingSeconds)}</strong>
                </TimerClock>
                <TimerState $running={timerRunning}>
                  {timerRunning ? 'Temporizador ativo' : 'Temporizador pausado'}
                </TimerState>
              </TimerStatus>
              <TimerActions>
                <Button type="button" variant="outline-secondary" onClick={handleSaveTimer}>
                  Salvar intervalo
                </Button>
                <Button type="button" variant={timerRunning ? 'secondary' : 'primary'} onClick={onToggleTimer}>
                  {timerRunning ? <PauseIcon style={{ fontSize: 14 }} /> : <PlayArrowIcon style={{ fontSize: 14 }} />}
                  <span>{timerRunning ? 'Pausar' : 'Iniciar'}</span>
                </Button>
                <Button type="button" variant="outline-primary" onClick={onRefreshTimer} disabled={timerLoading}>
                  <Icon name="reload" size={14} />
                  <span>Atualizar agora</span>
                </Button>
              </TimerActions>
            </TimerPanel>
          </Tab>
        </SettingsTabs>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline-secondary" onClick={handleReset}>
          Restaurar padrao
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="button" variant="primary" onClick={handleSave}>
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DashboardSettingsModal.propTypes = {
  show: PropTypes.bool,
  values: PropTypes.shape(),
  timerSettings: PropTypes.shape({
    intervalMinutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  timerRunning: PropTypes.bool,
  timerRemainingSeconds: PropTypes.number,
  timerLoading: PropTypes.bool,
  cardOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  onClose: PropTypes.func.isRequired,
  onSaveCards: PropTypes.func.isRequired,
  onSaveTimer: PropTypes.func.isRequired,
  onToggleTimer: PropTypes.func.isRequired,
  onRefreshTimer: PropTypes.func.isRequired
};

DashboardSettingsModal.defaultProps = {
  show: false,
  values: getDefaultDashboardCardVisibility(),
  timerSettings: {
    intervalMinutes: 5
  },
  timerRunning: false,
  timerRemainingSeconds: 0,
  timerLoading: false,
  cardOptions: dashboardCardVisibilityOptions
};

export default DashboardSettingsModal;

const SettingsTabs = styled(Tabs)`
  .nav-link {
    font-size: 13px;
    color: #4b5563;
  }

  .nav-link.active {
    color: #233980;
    font-weight: 600;
  }
`;

const TabIntro = styled.div`
  margin-bottom: 12px;
  color: #6b7280;
  font-size: 13px;
`;

const CardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CardOption = styled.div`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;

  label {
    cursor: pointer;
    color: #374151;
    font-size: 13px;
  }
`;

const TimerPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TimerHint = styled.div`
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
`;

const TimerStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
`;

const TimerClock = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #233980;

  strong {
    font-size: 16px;
    font-variant-numeric: tabular-nums;
  }
`;

const TimerState = styled.div`
  color: ${({ $running }) => ($running ? '#1f7a3f' : '#6b7280')};
  font-size: 12px;
  font-weight: 600;
`;

const TimerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;
